import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dropdown, Button, Avatar, Divider } from 'antd';
import { UserOutlined, LogoutOutlined } from '@ant-design/icons';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';

import { ROUTES } from '@config';

import { useUser, useLogout } from '@hooks';

import styles from './UserDropdown.module.scss';

type UserDropdownProps = {
  collapsed: boolean;
};

const UserDropdown = ({ collapsed }: UserDropdownProps) => {
  const { _ } = useLingui();
  const [open, setOpen] = useState(false);
  const user = useUser();
  const logout = useLogout();
  const navigate = useNavigate();

  const dropdownContent = (
    <div className={styles.dropdownContent}>
      <div className={styles.row}>
        <Avatar src={user.avatar} size={40} icon={!user.avatar && <UserOutlined />} />
        <div className={styles.column}>
          <span className={styles.userName}>
            {user.first_name} {user.last_name}
          </span>
          <span className={styles.userEmail}>{user.email}</span>
        </div>
      </div>
      <Divider className={styles.divider} />
      <Button
        className={styles.dropdownButton}
        type="text"
        icon={<UserOutlined />}
        onClick={() => {
          navigate(ROUTES.PROFILE);
          setOpen(false);
        }}
      >
        <span>
          <Trans>Settings</Trans>
        </span>
      </Button>
      <Button
        className={styles.dropdownButton}
        type="text"
        icon={<LogoutOutlined />}
        onClick={() => {
          logout();
          setOpen(false);
        }}
      >
        <span>
          <Trans>Log out</Trans>
        </span>
      </Button>
    </div>
  );

  if (collapsed) {
    return (
      <Dropdown
        open={open}
        onOpenChange={(open: boolean) => setOpen(open)}
        trigger={['hover']}
        placement="bottomRight"
        dropdownRender={() => dropdownContent}
      >
        <Button
          className={styles.userButtonCollapsed}
          shape="circle"
          icon={<UserOutlined />}
          aria-label={_(msg`Open user menu`)}
          data-testid="user-button"
        />
      </Dropdown>
    );
  }

  return (
    <div className={styles.sidebarUserSection}>
      <Dropdown
        open={open}
        onOpenChange={(open: boolean) => setOpen(open)}
        trigger={['click']}
        placement="topLeft"
        dropdownRender={() => dropdownContent}
      >
        {/* Same handle as the collapsed variant's button, so callers (and tests)
            have one way to open the user menu in either sidebar state. */}
        <div className={styles.userInfo} data-testid="user-button">
          <Avatar src={user.avatar} size={32} icon={!user.avatar && <UserOutlined />} />
          <div className={styles.userDetails}>
            <span className={styles.userNameSidebar}>
              {user.first_name} {user.last_name}
            </span>
            <span className={styles.userEmailSidebar}>{user.email}</span>
          </div>
        </div>
      </Dropdown>
    </div>
  );
};

export default UserDropdown;
